import React, { useState, useMemo } from 'react';
import { X, Clock, Settings2, RotateCcw, Check, XCircle, AlertTriangle, ChevronLeft, ChevronRight, Repeat, Calendar } from 'lucide-react';

const DAYS_PER_PAGE = 30;

/** Format YYYY-MM-DD to DD/MM/YYYY */
const formatDateBR = (dateStr) => {
  if (!dateStr || dateStr.length !== 10) return dateStr || '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const RECURRENCE_LABELS = {
  once: 'Envio único',
  daily: 'Diário',
  weekly: 'Semanal',
  monthly: 'Mensal',
};

const RecipientDetailModal = ({ isOpen, phone, history, followup, onClose, onResetRecipient, onResetDay, onRefresh, colors }) => {
  const [activeTab, setActiveTab] = useState('history');
  const [dayPage, setDayPage] = useState(1);
  const [resettingDay, setResettingDay] = useState(null);
  const [resettingAll, setResettingAll] = useState(false);

  if (!isOpen) return null;

  const isScheduledMessage = followup?.type === 'scheduled_message';
  const totalDays = followup?.total_days || 0;

  // Find the recipient data object to get schedule_override
  const recipientData = (followup?.recipients || []).find(r => {
    const rPhone = typeof r === 'string' ? r : r?.phone;
    return rPhone === phone;
  });
  const scheduleOverride = typeof recipientData === 'object' ? recipientData?.schedule_override : null;
  const effectiveSchedule = scheduleOverride || followup?.schedule || {};

  const recipientHistory = (history || [])
    .filter(h => h.recipient === phone)
    .sort((a, b) => {
      if (a.day !== b.day) return a.day - b.day;
      return new Date(a.sent_at) - new Date(b.sent_at);
    });

  // Consolidate by day
  const dayMap = {};
  recipientHistory.forEach(h => {
    if (!dayMap[h.day]) {
      dayMap[h.day] = { day: h.day, total: 0, sent: 0, failed: 0, lastDate: h.sent_at, isTest: h.is_test, errors: [] };
    }
    dayMap[h.day].total++;
    if (h.status === 'sent') dayMap[h.day].sent++;
    else {
      dayMap[h.day].failed++;
      if (h.error) dayMap[h.day].errors.push(h.error);
    }
    if (new Date(h.sent_at) > new Date(dayMap[h.day].lastDate)) {
      dayMap[h.day].lastDate = h.sent_at;
    }
  });

  const dayRows = Object.values(dayMap).sort((a, b) => a.day - b.day);
  const sentDaysSet = new Set(dayRows.filter(r => r.sent > 0).map(r => r.day));
  const failedDaysSet = new Set(dayRows.filter(r => r.failed > 0 && r.sent === 0).map(r => r.day));

  const totalSent = recipientHistory.filter(h => h.status === 'sent').length;
  const totalFailed = recipientHistory.filter(h => h.status !== 'sent').length;

  // Day management pagination
  const totalDayPages = Math.max(1, Math.ceil(totalDays / DAYS_PER_PAGE));
  const currentDayPage = Math.min(dayPage, totalDayPages);
  const dayStart = (currentDayPage - 1) * DAYS_PER_PAGE + 1;
  const dayEnd = Math.min(currentDayPage * DAYS_PER_PAGE, totalDays);

  const handleResetDay = async (day) => {
    if (!window.confirm(`Resetar histórico do dia ${day} para este destinatário?`)) return;
    setResettingDay(day);
    try {
      await onResetDay(day);
    } finally {
      setResettingDay(null);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm(`Resetar TODO o histórico de ${phone}? Todos os dias serão desmarcados.`)) return;
    setResettingAll(true);
    try {
      await onResetRecipient(phone);
    } finally {
      setResettingAll(false);
    }
  };

  const tabs = [
    { id: 'history', label: 'Histórico', icon: Clock },
    { id: 'manage', label: 'Gerenciar Dias', icon: Settings2 },
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '16px',
        width: '100%', maxWidth: '640px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                Detalhes do destinatário
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: colors.textSecondary, fontFamily: 'monospace' }}>
                {phone}
              </p>
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
              padding: '8px', borderRadius: '8px',
            }}>
              <X size={20} />
            </button>
          </div>

          {/* Schedule info for scheduled_message */}
          {isScheduledMessage && (
            <div style={{
              display: 'flex', gap: '10px', marginTop: '12px',
              padding: '10px 12px', borderRadius: '10px',
              backgroundColor: '#0ea5e908', border: '1px solid #0ea5e915',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0ea5e9' }}>
                <Clock size={12} /> {effectiveSchedule.send_time || '09:00'}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0ea5e9' }}>
                <Repeat size={12} /> {RECURRENCE_LABELS[effectiveSchedule.recurrence] || 'Uma vez'}
              </span>
              {effectiveSchedule.send_date && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0ea5e9' }}>
                  <Calendar size={12} /> {formatDateBR(effectiveSchedule.send_date)}
                </span>
              )}
              {scheduleOverride && (
                <span style={{
                  padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: 600,
                  backgroundColor: '#0ea5e920', color: '#0ea5e9',
                }}>
                  Override
                </span>
              )}
            </div>
          )}

          {/* Summary stats */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
            {!isScheduledMessage && (
              <div style={{
                flex: 1, padding: '10px', borderRadius: '10px',
                backgroundColor: colors.bgSecondary, textAlign: 'center',
              }}>
                <div style={{ fontSize: '18px', fontWeight: 700, color: colors.accentPrimary }}>
                  {sentDaysSet.size}/{totalDays}
                </div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Dias recebidos</div>
              </div>
            )}
            <div style={{
              flex: 1, padding: '10px', borderRadius: '10px',
              backgroundColor: colors.bgSecondary, textAlign: 'center',
            }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                {totalSent}
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary }}>Msgs enviadas</div>
            </div>
            <div style={{
              flex: 1, padding: '10px', borderRadius: '10px',
              backgroundColor: colors.bgSecondary, textAlign: 'center',
            }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: totalFailed > 0 ? '#ef4444' : '#22c55e' }}>
                {totalFailed}
              </div>
              <div style={{ fontSize: '11px', color: colors.textSecondary }}>Falhas</div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: '4px', marginTop: '12px' }}>
            {tabs.filter(tab => !(isScheduledMessage && tab.id === 'manage')).map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    padding: '8px 12px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 500, transition: 'all 0.15s',
                    backgroundColor: isActive ? `${colors.accentPrimary}15` : 'transparent',
                    color: isActive ? colors.accentPrimary : colors.textSecondary,
                  }}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {activeTab === 'history' && (
            <>
              {dayRows.length === 0 ? (
                <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '13px', padding: '20px 0' }}>
                  Nenhum envio registrado.
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                      <th style={{ textAlign: 'left', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Dia</th>
                      <th style={{ textAlign: 'center', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Msgs</th>
                      <th style={{ textAlign: 'center', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Status</th>
                      <th style={{ textAlign: 'right', padding: '8px', color: colors.textSecondary, fontWeight: 500 }}>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayRows.map(row => {
                      const allSent = row.failed === 0;
                      const allFailed = row.sent === 0;
                      const statusLabel = allSent ? 'Enviado' : allFailed ? 'Falhou' : 'Parcial';
                      const statusColor = allSent ? '#22c55e' : allFailed ? '#ef4444' : '#f59e0b';
                      return (
                        <tr key={row.day} style={{ borderBottom: `1px solid ${colors.borderColor}20` }}>
                          <td style={{ padding: '8px', color: colors.textPrimary, fontWeight: 500 }}>
                            Dia {row.day}
                            {row.isTest && (
                              <span style={{ fontSize: '10px', color: colors.textTertiary, marginLeft: '6px' }}>teste</span>
                            )}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center', color: colors.textSecondary }}>
                            {row.sent}/{row.total}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                              backgroundColor: `${statusColor}20`, color: statusColor,
                            }}>
                              {statusLabel}
                            </span>
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', color: colors.textSecondary, fontSize: '11px' }}>
                            {new Date(row.lastDate).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}

          {activeTab === 'manage' && (
            <>
              {/* Reset all button */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '16px', padding: '12px', borderRadius: '10px',
                backgroundColor: colors.bgSecondary,
              }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Resetar follow-up completo
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textSecondary, marginTop: '2px' }}>
                    Remove todo histórico deste destinatário
                  </div>
                </div>
                <button
                  onClick={handleResetAll}
                  disabled={resettingAll || dayRows.length === 0}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 14px', border: `1px solid #ef444440`,
                    borderRadius: '8px', cursor: dayRows.length === 0 ? 'default' : 'pointer',
                    backgroundColor: 'transparent', color: '#ef4444',
                    fontSize: '12px', fontWeight: 500,
                    opacity: resettingAll || dayRows.length === 0 ? 0.5 : 1,
                  }}
                >
                  <RotateCcw size={13} style={resettingAll ? { animation: 'spin 1s linear infinite' } : {}} />
                  {resettingAll ? 'Resetando...' : 'Resetar tudo'}
                </button>
              </div>

              {/* Day cards grid */}
              {totalDays === 0 ? (
                <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '13px', padding: '20px 0' }}>
                  Nenhum dia configurado neste follow-up.
                </p>
              ) : (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                    gap: '8px',
                  }}>
                    {Array.from({ length: dayEnd - dayStart + 1 }, (_, i) => {
                      const dayNum = dayStart + i;
                      const isSent = sentDaysSet.has(dayNum);
                      const isFailed = failedDaysSet.has(dayNum);
                      const dayInfo = dayMap[dayNum];
                      const isResetting = resettingDay === dayNum;
                      const hasMessages = followup?.days?.[String(dayNum)]?.messages?.length > 0;

                      let cardBg = colors.bgSecondary;
                      let cardBorder = colors.borderColor;
                      let statusIcon = null;
                      let statusText = 'Pendente';
                      let statusColor = colors.textTertiary;

                      if (isSent) {
                        cardBg = '#22c55e10';
                        cardBorder = '#22c55e40';
                        statusIcon = <Check size={12} />;
                        statusText = 'Enviado';
                        statusColor = '#22c55e';
                      } else if (isFailed) {
                        cardBg = '#ef444410';
                        cardBorder = '#ef444440';
                        statusIcon = <XCircle size={12} />;
                        statusText = 'Falhou';
                        statusColor = '#ef4444';
                      } else if (!hasMessages) {
                        statusIcon = <AlertTriangle size={12} />;
                        statusText = 'Sem msgs';
                        statusColor = colors.textTertiary;
                      }

                      return (
                        <div
                          key={dayNum}
                          style={{
                            padding: '10px 8px',
                            borderRadius: '10px',
                            backgroundColor: cardBg,
                            border: `1px solid ${cardBorder}`,
                            textAlign: 'center',
                            position: 'relative',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div style={{
                            fontSize: '15px', fontWeight: 700, color: colors.textPrimary,
                            marginBottom: '4px',
                          }}>
                            {dayNum}
                          </div>
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                            fontSize: '10px', fontWeight: 500, color: statusColor,
                          }}>
                            {statusIcon}
                            {statusText}
                          </div>
                          {dayInfo && (
                            <div style={{
                              fontSize: '9px', color: colors.textTertiary, marginTop: '3px',
                            }}>
                              {new Date(dayInfo.lastDate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                            </div>
                          )}
                          {(isSent || isFailed) && (
                            <button
                              onClick={() => handleResetDay(dayNum)}
                              disabled={isResetting}
                              title="Resetar este dia"
                              style={{
                                position: 'absolute', top: '4px', right: '4px',
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: colors.textTertiary, padding: '2px', borderRadius: '4px',
                                opacity: isResetting ? 0.4 : 0.6,
                                transition: 'opacity 0.15s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
                            >
                              <RotateCcw size={11} style={isResetting ? { animation: 'spin 1s linear infinite' } : {}} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Day pagination */}
                  {totalDayPages > 1 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: '12px', marginTop: '14px',
                    }}>
                      <button
                        onClick={() => setDayPage(p => Math.max(1, p - 1))}
                        disabled={currentDayPage === 1}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          background: 'none', border: `1px solid ${colors.borderColor}`,
                          borderRadius: '6px', padding: '4px 10px', fontSize: '12px',
                          color: colors.textSecondary, cursor: currentDayPage === 1 ? 'default' : 'pointer',
                          opacity: currentDayPage === 1 ? 0.4 : 1,
                        }}
                      >
                        <ChevronLeft size={13} />
                      </button>
                      <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                        Dias {dayStart}-{dayEnd} de {totalDays}
                      </span>
                      <button
                        onClick={() => setDayPage(p => Math.min(totalDayPages, p + 1))}
                        disabled={currentDayPage === totalDayPages}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          background: 'none', border: `1px solid ${colors.borderColor}`,
                          borderRadius: '6px', padding: '4px 10px', fontSize: '12px',
                          color: colors.textSecondary, cursor: currentDayPage === totalDayPages ? 'default' : 'pointer',
                          opacity: currentDayPage === totalDayPages ? 0.4 : 1,
                        }}
                      >
                        <ChevronRight size={13} />
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RecipientDetailModal;
